import { AppUrl, CdnUrl } from "../../../common/constants";

export abstract class BaseApiClient {
    private _url: string;

    constructor(url: string) {
        this._url = url;
    }

    async getEmpty(path: string, params?: Record<string, string>): Promise<ApiResponse> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async getText(path: string, params?: Record<string, string>): Promise<string> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await res.text()
    }

    async get<T>(path: string, params?: Record<string, string>): Promise<ApiDataResponse<T>> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async getSimple<T>(path: string, params?: Record<string, string>): Promise<T> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafeGeneric(res);
    }

    async getWithPagination<T>(path: string, page: number, params?: Record<string, string | undefined>, count?: number)
        : Promise<ApiPaginationResponse<T>> {

        if (params) {
            params['page'] = page.toString();
        } else {
            params = {
                page: page.toString()
            }
        }

        if (count) {
            params['count'] = count.toString();
        }

        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafePaggination(res);
    }

    async getBlob(path: string, params?: Record<string, string | undefined>): Promise<Blob> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'GET');

        const res = await fetch(url, {
            method: 'GET',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await res.blob();
    }

    async delete(path: string, params?: Record<string, string>): Promise<ApiResponse> {
        const url = this.getUrl(path, params);
        const headers = this.getDefaultHeaders(url, 'DELETE');

        const res = await fetch(url, {
            method: 'DELETE',
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async postOrPut<TRequest, ApiResponse>(path: string, model: TRequest, method: 'POST' | 'PUT')
        : Promise<ApiDataResponse<ApiResponse>> {
        const url = this.getUrl(path);
        const headers = this.getDefaultHeaders(url, method);

        const res = await fetch(url, {
            method: method,
            headers: headers,
            body: JSON.stringify(model)
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async postOrPutEmptyResponse<TRequest>(path: string, model: TRequest, method: 'POST' | 'PUT')
        : Promise<ApiResponse> {
        const url = this.getUrl(path);
        const headers = this.getDefaultHeaders(url, method);

        const res = await fetch(url, {
            method: method,
            headers: headers,
            body: JSON.stringify(model)
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async postOrPutEmpty(path: string, method: 'POST' | 'PUT')
        : Promise<ApiResponse> {
        const url = this.getUrl(path);
        const headers = this.getDefaultHeaders(url, method);

        const res = await fetch(url, {
            method: method,
            headers: headers
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    async postOrPutFullEmpty(path: string, method: 'POST' | 'PUT'): Promise<Response> {
        const url = this.getUrl(path);
        const headers = this.getDefaultHeaders(url, method);

        const res = await fetch(url, {
            method,
            headers
        });

        await this.validateAuthUserResponse(res);
        return res;
    }

    async postFormData(path: string, fields: KeyValuePair<string, any>[], requestMethod?: 'POST' | 'PUT')
        : Promise<ApiResponse> {
        const method = requestMethod || 'POST';
        const url = this.getUrl(path);
        const headers = this.getDefaultHeaders(url, method, 'formData');

        const formData = new FormData();
        fields?.forEach(f => formData.append(f.key, f.value));

        const res = await fetch(url, {
            method: method,
            headers: headers,
            body: formData
        });
        this.validateAuthUserResponse(res);

        return await this.parseJsonSafe(res);
    }

    private getUrl(path: string, params?: Record<string, string | undefined>): string {
        let url = `${this._url}/${path}`;

        if (params) {
            const searchParams = new URLSearchParams();
            for (const [key, value] of Object.entries(params)) {
                if (value !== undefined) {
                    searchParams.append(key, value);
                }
            }

            const queryString = searchParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }

        return url;
    }


    private validateAuthUserResponse(response: Response) {
    }

    private async parseJsonSafe(res: Response): Promise<ApiResponse> {
        const text = await res.text();

        if (!text)
            return {
                hasErrors: false,
                errors: []
            };

        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn('Ошибка парсинга JSON:', e);
            return {
                hasErrors: true,
                errors: []
            };
        }
    }

    private async parseJsonSafePaggination<T>(res: Response): Promise<ApiPaginationResponse<T>> {
        const text = await res.text();

        if (!text)
            return {
                data: {
                    isLastPage: true,
                    totalCount: 0,
                    totalPages: 0,
                    values: []
                },
                hasErrors: false,
                errors: []
            };

        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn('Ошибка парсинга JSON:', e);
            return {
                data: {
                    isLastPage: true,
                    totalCount: 0,
                    totalPages: 0,
                    values: []
                },
                hasErrors: false,
                errors: []
            };
        }
    }

    private async parseJsonSafeGeneric<T>(res: Response): Promise<T> {
        const text = await res.text();

        if (!text)
            return {} as T;

        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn('Ошибка парсинга JSON:', e);
            return {} as T;
        }
    }

    private getDefaultHeaders(url: string, method: 'GET' | 'DELETE' | 'POST' | 'PUT', contentType?: 'json' | 'formData'): HeadersInit {
        const cookie = window.document.cookie;
        contentType = contentType ?? 'json';

        const isCdn = url.startsWith(CdnUrl)

        let baseHeaders: Record<string, string> = {
            'Cookie': cookie,
            'Accept': '*/*'
        };

        const token = getCookie('token');

        if (token && !isCdn) {
            baseHeaders = {
                ...baseHeaders,
                'Authorization': token
            }
        }

        if ((method === 'GET' || method === 'DELETE')) {

            if (isCdn) {
                return {
                    ...baseHeaders,
                };
            } else {
                return {
                    ...baseHeaders,
                    'Access-Control-Allow-Origin': AppUrl,
                };
            }
        }

        if (contentType === 'json') {
            return {
                ...baseHeaders,
                'Accept': '*/*',
                'Content-Type': 'application/json',
            };
        }

        return baseHeaders;

        function getCookie(name: string): string | null {
            const match = cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
            return match ? decodeURIComponent(match[2]) : null;
        }
    };
}




